import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Tag, Task } from 'app/modules/admin/tasks/tasks.types';
import { environment } from 'environments/environment';
import { AuthService } from 'app/core/auth/auth.service';

@Injectable({
    providedIn: 'root'
})
export class TasksService {
    baseUrl = environment.apiBaseUrl;
    httpOptions: any = {
        withCredentials: true,
        observe: 'response' as 'response'
    };
    user: any;
    private _tags: BehaviorSubject<Tag[] | null> = new BehaviorSubject(null);
    private _task: BehaviorSubject<Task | null> = new BehaviorSubject(null);
    private _tasks: BehaviorSubject<Task[] | null> = new BehaviorSubject(null);
    private _tasksNotification: BehaviorSubject<Task[] | null> = new BehaviorSubject(null);

    constructor(
        private _httpClient: HttpClient,
        private authService: AuthService,
    ) {
        this.user = authService.localUser;

    }

    get tags$(): Observable<Tag[]> {
        return this._tags.asObservable();
    }

    get task$(): Observable<Task> {
        return this._task.asObservable();
    }

    get tasks$(): Observable<Task[]> {
        return this._tasks.asObservable();
    }

    get tasksNotification$(): Observable<Task[]> {
        return this._tasksNotification.asObservable();
    }

    getTags(): Observable<any[]> {
        return this._httpClient.get<any[]>(`${this.baseUrl}api/data/admin/tag?find[userId]=${this.user.userID}`).pipe(
            tap((response: any) => {
                this._tags.next(response.items);
            })
        );
    }

    createTag(tag: Tag): Observable<any> {
        return this.tags$.pipe(
            take(1),
            switchMap(tags => this._httpClient.post<any>(`${this.baseUrl}api/data/admin/tag`, tag).pipe(
                map((newTag) => {
                    this._tags.next([...tags, newTag.item]);
                    return newTag.item;
                })
            ))
        );
    }

    createTaskTag(tagId, actionItemId): Observable<any> {
        return this._httpClient.post<any>(`${this.baseUrl}api/data/admin/action-item-tag`, { tagId: tagId, actionItemId: actionItemId }).pipe(
            map((newTag) => {
                return newTag.item;
            })
        );
    }

    deleteTaskTag(id): Observable<any> {
        return this._httpClient.delete<any>(`${this.baseUrl}api/data/admin/action-item-tag/${id}?find[userId]=${this.user.userID}`).pipe(
            map((newTag) => {
                return newTag.item;
            })
        );
    }

    updateTag(id: string, tag: Tag): Observable<Tag> {
        return this.tags$.pipe(
            take(1),
            switchMap(tags => this._httpClient.put<Tag>(`${this.baseUrl}api/data/admin/tag/${id}`, tag).pipe(
                map((updatedTag) => {
                    const index = tags.findIndex(item => item.id === id);
                    tags[index] = updatedTag;
                    this._tags.next(tags);
                    return updatedTag;
                })
            ))
        );
    }

    deleteTag(id: string): Observable<boolean> {
        return this.tags$.pipe(
            take(1),
            switchMap(tags => this._httpClient.delete(`${this.baseUrl}api/data/admin/tag/${id}`, { params: { id } }).pipe(
                map((isDeleted: boolean) => {
                    const index = tags.findIndex(item => item.id === id);
                    tags.splice(index, 1);
                    this._tags.next(tags);
                    return isDeleted;
                }),
                filter(isDeleted => isDeleted),
                switchMap(isDeleted => this.tasks$.pipe(
                    take(1),
                    map((tasks) => {
                        tasks.forEach((task) => {
                            const tagIndex = task?.tags?.findIndex(tag => tag === id);
                            if (tagIndex > -1) {
                                task.tags.splice(tagIndex, 1);
                            }
                        });
                        return isDeleted;
                    })
                ))
            ))
        );
    }

    getTasks(): Observable<any[]> {
        return this._httpClient.get<Task[]>(`${this.baseUrl}api/data/admin/action-item?find[userId]=${this.user.userID}`, {
            params: {
                'itemsPerPage': environment.defaultAllItemsPerPage,
                // 'sort[ParentId]': 'asc',
                'sort[SortOrder]': 'asc',
                // 'sort[Created]': 'asc',
            }
        }).pipe(
            tap((response: any) => {
                this._tasks.next(response.items);
            })
        );
    }

    getTasksPublic(): Observable<any[]> {
        return this._httpClient.get<Task[]>(`${this.baseUrl}api/data/action-item-view?find[userId]=${this.user.userID}`, {
            params: {
                'itemsPerPage': environment.defaultAllItemsPerPage,
                // 'sort[ParentId]': 'asc',
                'sort[SortOrder]': 'asc',
                // 'sort[Created]': 'asc',
            }
        }).pipe(
            tap((response: any) => {
                this._tasks.next(response.items);
                return response;
            })
        );
    }

    getTasksPublicForNotification(checkForClientOnly: boolean = false, clientId: any = ''): Observable<any[]> {
        let url = `${this.baseUrl}api/data/action-item-view?find[userId]=${this.user.userID}`;
        if (checkForClientOnly) {
            url = `${this.baseUrl}api/data/action-item-view?find[SubjectType]=Client&find[SubjectId]=${clientId}`;
        }
        return this._httpClient.get<Task[]>(url, {
            params: {
                'itemsPerPage': environment.defaultAllItemsPerPage,
                'find[Type:in]': ['task', 'alert'].join(),
                // 'find[Type]': 'task',
                // 'orWhere[Type]': 'alert',
                'find[Title]': 'IS NOT NULL',
                // 'sort[ParentId]': 'asc',
                // 'sort[SortOrder]': 'asc',
                'sort[Created]': 'desc',
            }
        }).pipe(
            tap((response: any) => {
                this._tasksNotification.next(response.items);
                return response;
            })
        );
    }

    getTasksPublicForNotificationForSubjects(id, type, order = 'desc',currentPage:number = 1): Observable<any[]> {
        return this._httpClient.get<Task[]>(`${this.baseUrl}api/client/action-item-view`, {
            params: {
                'itemsPerPage': environment.defaultAllItemsPerPage,
                'currentPage': currentPage,
                'find[Type:in]': ['task', 'alert', 'message'].join(),
                'find[Title]': 'IS NOT NULL',
                'find[SubjectId]': id,
                'find[SubjectType]': type,
                'sort[Created]': order,
            }
        }).pipe(
            tap((response: any) => {
                return response;
            })
        );
    }

    updateTasksOrdersAPI(tasks): Observable<Task[]> {
        return this._httpClient.post<any>(`${this.baseUrl}api/data/admin/action-item/update/order?find[userId]=${this.user.userID}`, { tasks });
    }

    updateTasksOrders(tasks: Task[]): Observable<Task[]> {
        return this._httpClient.patch<Task[]>('api/apps/tasks/order', { tasks });
    }

    searchTasks(query: string): Observable<Task[] | null> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/admin/action-item?find[userId]=${this.user.userID}`, {
            params: {
                'sort[SortOrder]': 'asc',
                'like[title]': query
            }
        });
    }

    getTaskTagsByTaskId(id: string): Observable<any> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/admin/action-item-tag?find[userId]=${this.user.userID}`, {
            params: {
                'find[actionItemId]': id
            }
        }).pipe(
            switchMap((taskTags) => {
                // const index = tasks.findIndex(item => item.id === id);
                // this._task.next(task);

                return of(taskTags.items);
            })
        );
    }

    getTaskById(id: string): Observable<Task> {
        return this._tasks.pipe(
            take(1),
            map((tasks) => {
                const task = tasks.find(item => item.id === id) || null;
                this._task.next(task);
                return task;
            }),
            switchMap((task) => {
                if (!task) {
                    return throwError('Could not found task with id of ' + id + '!');
                }
                return of(task);
            })
        );
    }

    createTask(type: string, parentId: string = null): Observable<any> {
        return this.tasks$.pipe(
            take(1),
            switchMap(tasks => this._httpClient.post<any>(`${this.baseUrl}api/data/admin/action-item`, {
                type: type,
                parentId: parentId,
                title: '',
                notes: null,
                completed: false,
                dueDate: null,
                completeAt: null,
                priority: 1,
                sortOrder: tasks?.length || 0,
            }).pipe(
                map((newTask) => {
                    this._tasks.next([newTask?.item, ...tasks]);
                    return newTask?.item;
                })
            ))
        );
    }

    updateTask(id: string, task: Task, role: string = 'admin'): Observable<any> {
        delete task.created;
        delete task.modified;
        return this.tasks$
            .pipe(
                take(1),
                switchMap(tasks => this._httpClient.put<any>(`${this.baseUrl}api/data/${role}/action-item/${id}`, task).pipe(
                    map((updatedTask) => {
                        const index = tasks?.findIndex(item => item.id === id);
                        if (index && tasks && tasks?.length > 0) {
                            tasks[index] = updatedTask?.item;
                            this._tasks.next(tasks);
                        }
                        return updatedTask.item;
                    }),
                    switchMap(updatedTask => this.task$.pipe(
                        take(1),
                        filter(item => item && item.id === id),
                        tap(() => {
                            this._task.next(updatedTask);
                            return updatedTask;
                        })
                    ))
                ))
            );
    }

    updateTaskNotification(id: string, task: Task, role: string = 'admin'): Observable<any> {
        delete task.created;
        delete task.modified;
        return this.tasksNotification$
            .pipe(
                take(1),
                switchMap(tasks => this._httpClient.put<any>(`${this.baseUrl}api/data/${role}/action-item/${id}`, task).pipe(
                    map((updatedTask) => {
                        const index = tasks?.findIndex(item => item.id === id);
                        if (index && tasks && tasks?.length > 0) {
                            tasks[index] = updatedTask?.item;
                            this._tasksNotification.next(tasks);
                        }
                        return updatedTask.item;
                    }),
                    switchMap(updatedTask => this.task$.pipe(
                        take(1),
                        filter(item => item && item.id === id),
                        tap(() => {
                            this._task.next(updatedTask);
                            return updatedTask;
                        })
                    ))
                ))
            );
    }

    createTaskMessage(task: Task, role: string = 'admin'): Observable<any> {
        delete task.created;
        delete task.modified;
        return this.tasks$
            .pipe(
                take(1),
                switchMap(tasks => this._httpClient.post<any>(`${this.baseUrl}api/data/${role}/action-item`, task).pipe(
                    map((updatedTask) => {
                        return updatedTask.item;
                    })
                ))
            );
    }

    deleteTask(id: string): Observable<boolean> {
        return this.tasks$.pipe(
            take(1),
            switchMap(tasks => this._httpClient.delete(`${this.baseUrl}api/data/admin/action-item/${id}?find[userId]=${this.user.userID}`, { params: { id } }).pipe(
                map((isDeleted: boolean) => {
                    const index = tasks.findIndex(item => item.id === id);
                    tasks.splice(index, 1);
                    this._tasks.next(tasks);
                    return isDeleted;
                })
            ))
        );
    }

    markAllAsRead(tasks): Observable<boolean> {
        return this._httpClient.post<any>(`${this.baseUrl}api/data/admin/action-item/update/read-unread?find[userId]=${this.user.userID}`, { tasks });
    }

    markAsAllReadMessage(tasks): Observable<boolean> {
        return this._httpClient.post<any>(`${this.baseUrl}api/data/admin/action-item/update/read-unread`, { tasks });
    }

    getTasksPublicForNotificationForDashboard(id: number[], order: string = 'desc', isNull: boolean = true): Observable<any[]> {
        let params: any = {
            'itemsPerPage': environment.defaultAllItemsPerPage,
            'find[Type:in]': ['message'].join(),
            'find[Title]': 'IS NOT NULL',
            'find[SubjectId:in]': id.join(),
            'sort[Created]': order,
        };

        if (isNull) {
            params['find[readAt]'] = 'IS NULL';
        }

        return this._httpClient.get<Task[]>(`${this.baseUrl}api/client/action-item-view`, { params })
        .pipe(
            tap((response: any) => {
                return response;
            })
        );
    }
}
